import React from 'react';
import classNames from 'classnames';
import { useEnvironment } from '@wix/yoshi-flow-editor';
import {
  getPostCover,
  isLayoutNameMobile,
  isLayoutNameOneOfProGallery,
  isLayoutNameTextOnImage,
  LAYOUT_NAME_SIDE_BY_SIDE,
  POST_EXCERPT_MAX_LENGTH,
  resolveId,
} from '@wix/communities-blog-client-common';
import ContentExcerpt from '@app/external/common/components/content-excerpt';
import Link from '@app/external/common/components/link/internal-link';
import { PostFooter } from '@app/external/common/components/post-footer';
import PostListItemCategoryLabel from '@app/external/common/components/post-list-item-category-label';
import PostListItemCover from '@app/external/common/components/post-list-item-cover';
import PostListItemHeader from '@app/external/common/components/post-list-item-header';
import PostTitle from '@app/external/common/components/post-title';
import RatingsDisplay, {
  RatingsDisplayLayout,
} from '@app/external/common/components/ratings-display';
import ResponsiveContentExcerpt from '@app/external/common/components/responsive-content-excerpt';
import { useActions, useSelector } from '@app/external/common/components/runtime-context';
import { HorizontalSeparatorForPostCard } from '@app/external/common/components/separator';
import useDeviceType from '@app/external/common/hooks/use-device-type';
import { useFeedBorderWidth } from '@app/external/common/hooks/use-feed-border-width';
import { useFeedMetadataSettings } from '@app/external/common/hooks/use-feed-metadata-settings';
import useFontClassName from '@app/external/common/hooks/use-font-class-name';
import useIsFeedDesignEnabled from '@app/external/common/hooks/use-is-feed-design-enabled';
import usePermissions from '@app/external/common/hooks/use-permissions';
import usePostFontSize from '@app/external/common/hooks/use-post-font-size';
import {
  getIsMoreButtonEnabled,
  getMobileContentAlignment,
} from '@app/external/common/selectors/app-settings-selectors';
import { getPostCoverImageSrc } from '@app/external/common/selectors/post-selectors';
import { getContentAlignmentStyles } from '@app/external/common/services/content-alignment-helpers';
import { getFeedColorClassName, getLineCounts } from '@app/external/common/services/layout-config';
import { getPostActions } from '@app/external/common/services/post-actions';
import {
  getPostAverageRating,
  getPostTotalRatings,
} from '@app/external/common/store/post-ratings/post-ratings-selector';
import alignmentStyles from '../../../common/styles/post-list-item-alignment.scss';
import styles from '../../../common/styles/post-list-item.scss';
import { type LayoutConfigItemComponentProps } from '../post-list/layout-config-component-props';

const PostListItem: React.FC<LayoutConfigItemComponentProps> = ({
  type,
  post,
  itemConfig,
  index,
}) => {
  const { isRTL } = useEnvironment();
  const { contentFontClassNameWithStyle, titleFontClassName, contentFontClassName } =
    useFontClassName();
  const { postTitleFontSize, postDescriptionFontSize, postMetadataFontSize } = usePostFontSize();
  const {
    showCategoryLabel,
    showPostRating,
    showPostDescription,
    isMetadataHeaderVisible = true,
    isMetadataFooterVisible = true,
  } = useFeedMetadataSettings({ post });

  const { borderWidth } = useFeedBorderWidth();
  const { can, canSee } = usePermissions();

  const { applyFeedDesign, getPostClassName, section } = useIsFeedDesignEnabled();

  const actions = useActions();
  const { isMobile } = useDeviceType();

  const slice = useSelector((state) => {
    const contentAlignmentMobile = getMobileContentAlignment(state, isRTL);
    const postActions = getPostActions({
      post,
      canSee,
      enableShare: true,
    });
    const showMoreButton = Boolean(postActions.length);

    return {
      showMoreButton,
      isMoreButtonEnabled: getIsMoreButtonEnabled(state, showMoreButton),
      alignment: getContentAlignmentStyles(contentAlignmentMobile, isRTL),
      postAverageRating: getPostAverageRating(state, resolveId(post)),
      postTotalRatings: getPostTotalRatings(state, resolveId(post)),
    };
  });

  const postCover = getPostCover(post) ?? undefined;
  const lineCounts = getLineCounts(itemConfig, postCover?.shouldRender);
  const postLink = `/${post.slug}`;
  const autoFit = type === LAYOUT_NAME_SIDE_BY_SIDE && postCover?.shouldRender;

  const isProGallery = isLayoutNameOneOfProGallery(type);

  const isMobileLayout = isLayoutNameMobile(type);
  const withoutFooter = !isMetadataFooterVisible;
  const withoutHeaderMetadata = !isMetadataHeaderVisible;
  const withoutDescription = !showPostDescription;
  const useResponsive = type === LAYOUT_NAME_SIDE_BY_SIDE;
  const containerClassName = classNames(
    styles.container,
    isProGallery && styles.proGallery,
    styles[type],
    alignmentStyles[slice.alignment],
    postCover?.shouldRender && styles.withCover,
    withoutFooter && styles.withoutFooter,
    contentFontClassName,
    'blog-text-color',
    'blog-card-background-color',
    'blog-card-border-color',
    'post-list-item',
    getPostClassName(
      'border-color',
      'post-container',
      getFeedColorClassName(type, 'background-color'),
    ),
    isMobileLayout && (index ?? 0) > 0 && styles.newPadding,
  );

  const contentWrapperClassName = classNames(
    styles.contentWrapper,
    styles[type],
    isRTL && alignmentStyles.isRTL,
    !applyFeedDesign && isLayoutNameTextOnImage(type) && styles.withBackground,
    postCover?.shouldRender && styles.withCover,
    withoutFooter && styles.withoutFooter,
    'post-list-item-wrapper',
    isLayoutNameTextOnImage(type) && getPostClassName('overlay-background-color'),
    getPostClassName(
      'description-font',
      getFeedColorClassName(type, 'description-color'),
      getFeedColorClassName(type, 'description-fill'),
    ),
    isMobileLayout ? styles.newPadding : styles.boxShadow,
  );

  const linkContainerClassName = classNames(
    isMobileLayout ? styles.mobileWrapper : styles.textWrapper,
    getPostClassName('link-hashtag-hover-color'),
    styles[type],
  );
  const linkClassName = classNames(
    styles.titleWrapper,
    isProGallery && styles.proGallery,
    styles[type],
    withoutHeaderMetadata && styles.withoutHeaderMetadata,
    post.isPinned && styles.withIcons,
    slice.showMoreButton && slice.isMoreButtonEnabled
      ? styles.withShowMoreButton
      : styles.withoutShowMoreButton,
  );
  const titleContainerClassName = classNames(
    styles.title,
    titleFontClassName,
    styles[type],
    alignmentStyles.textAlign,
    postCover?.shouldRender && styles.withCover,
    withoutFooter && styles.withoutFooter,
    withoutHeaderMetadata && styles.withoutHeaderMetadata,
    withoutDescription && withoutFooter && styles.withoutBottomMargin,
    withoutDescription && styles.withoutDescription,
    post.isPinned && styles.withIcons,
    slice.showMoreButton && slice.isMoreButtonEnabled
      ? styles.withShowMoreButton
      : styles.withoutShowMoreButton,
    showCategoryLabel && styles.withCategoryLabel,
  );
  const titleClassName = classNames(
    getPostClassName('title-font', getFeedColorClassName(type, 'title-color')),
    useResponsive && styles.responsiveTitle,
  );
  const categoryContainerClassName = classNames(
    styles.category,
    styles[type],
    withoutHeaderMetadata && styles.withoutHeaderMetadata,
  );
  const contentClassName = classNames(
    styles.content,
    styles[type],
    alignmentStyles.textAlign,
    withoutFooter && styles.withoutFooter,
    contentFontClassNameWithStyle,
    withoutHeaderMetadata && styles.withoutHeaderMetadata,
    getPostClassName('description-style-font'),
    styles.excerptLink,
  );
  const headerClassName = classNames(
    contentFontClassName,
    getPostClassName('description-font'),
    alignmentStyles.headerContainer,
  );
  const footerClassName = classNames(
    getPostClassName('description-font', getFeedColorClassName(type, 'description-color')),
  );
  const titleStyle = isMobileLayout ? { fontSize: postTitleFontSize } : {};
  const contentStyle =
    !applyFeedDesign || isMobileLayout ? { fontSize: postDescriptionFontSize } : {};

  const renderCover = () => {
    const coverProps = {
      post,
      postLink,
      type,
      canPlayVideo: true,
      isPublic: can('share', 'post', post),
      videoClassName: classNames(styles.videoEmbed),
      imageClassName: classNames(styles.image),
      useAlternateDimesions: withoutFooter,
      ...(useResponsive
        ? {
            height: 518,
            width: 690,
            withoutContainerStyle: true,
          }
        : {}),
    };

    const cover = <PostListItemCover {...coverProps} />;

    return useResponsive ? (
      <div className={styles.coverContainer}>
        <div className={styles.coverWrapper}>{cover}</div>
      </div>
    ) : (
      cover
    );
  };

  const containerStyle = isProGallery
    ? {
        ...(getPostCoverImageSrc(post) && { background: 'transparent' }),
        borderWidth: 0,
      }
    : {
        borderWidth,
      };

  return (
    <article className={containerClassName} style={containerStyle} data-hook="post-list-item">
      {postCover?.shouldRender && renderCover()}
      <div className={contentWrapperClassName}>
        <PostListItemHeader
          className={headerClassName}
          post={post}
          showMoreButton={slice.isMoreButtonEnabled}
          showProfileImage={!isLayoutNameTextOnImage(type)}
          style={{ fontSize: postMetadataFontSize }}
          type={type}
        />
        {showCategoryLabel && (
          <div className={categoryContainerClassName}>
            <PostListItemCategoryLabel
              className={alignmentStyles.categoryLabel}
              post={post}
              postListLayout={type}
            />
          </div>
        )}
        <div className={linkContainerClassName} style={{ display: 'flex-inline' }}>
          <Link
            fullRoute={post.link}
            to={postLink}
            className={linkClassName}
            addHoverClasses={false}
          >
            <div
              style={{ fontSize: postTitleFontSize }}
              className={titleContainerClassName}
              data-hook="post-list-item__title"
            >
              <PostTitle
                fullRoute={post.link}
                type={type}
                title={post.title}
                style={titleStyle}
                className={titleClassName}
                {...(useResponsive ? {} : { lineCount: lineCounts.title })}
                showCategoryLabel={showCategoryLabel}
              />
              {showPostRating && slice.postTotalRatings ? (
                <RatingsDisplay
                  className={classNames(
                    styles.rating,
                    styles[section],
                    styles[type],
                    alignmentStyles.rating,
                    {
                      [styles.withoutFooter]: withoutFooter,
                      [styles.withTextOnImageStyles]: isLayoutNameTextOnImage(type),
                    },
                  )}
                  rating={slice.postAverageRating}
                  count={slice.postTotalRatings}
                  layout={
                    isMobile ? RatingsDisplayLayout.with_range : RatingsDisplayLayout.count_only
                  }
                />
              ) : null}
            </div>
          </Link>
          {showPostDescription && lineCounts.description ? (
            <div
              style={contentStyle}
              className={contentClassName}
              data-hook="post-description"
              onClick={() => actions.navigateProGalleryWithinPostPage(post.slug!)}
            >
              {useResponsive ? (
                <ResponsiveContentExcerpt post={post} />
              ) : (
                <ContentExcerpt
                  post={post}
                  lineCount={lineCounts.description}
                  autoFit={autoFit}
                  maxLength={POST_EXCERPT_MAX_LENGTH}
                />
              )}
            </div>
          ) : null}
        </div>
        {isMetadataFooterVisible && (
          <div
            className={classNames(
              styles.footer,
              styles[type],
              !postCover?.shouldRender && styles.withoutCover,
            )}
          >
            {!isMobileLayout && (
              <HorizontalSeparatorForPostCard
                className={classNames(styles.separator, styles[type])}
              />
            )}
            <div style={{ fontSize: postMetadataFontSize }}>
              <PostFooter
                className={footerClassName}
                displayIcons={itemConfig.displayFooterIcons}
                post={post}
                type={type}
              />
            </div>
          </div>
        )}
        {isMobileLayout && (
          <HorizontalSeparatorForPostCard
            className={classNames(
              styles.separator,
              styles.mobileSeparator,
              styles[type],
              isMetadataFooterVisible && styles.withMetadata,
            )}
          />
        )}
      </div>
    </article>
  );
};

export default PostListItem;
